import { Badge, Gap, Modal, Tab, TabBar, Tabs, TabView, theme } from '@salecycle/ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { useConsentClientName, useImportConsentContext } from '../context/ImportConsentContext';
import { FileManagementRoute } from '../routes';
import { ConsentFileType, Green, RAGStatus } from '../types';

const FileItemsElement = styled.div`
  display: flex;
  flex: 1;
  flex-wrap: wrap;
`;

const FileElement = styled.div`
  margin-right: 10px;
`;

type Props = {
  fileType: ConsentFileType;
};

function ImportingFileList({ fileType }: Props) {
  const { fileData } = useImportConsentContext();

  return (
    <FileItemsElement>
      {fileData
        .filter(file => file.status.consentType === fileType)
        .map(file => (
          <div key={file.fileName}>
            <FileElement>
              <Badge color={theme.vars.newColor.green2}>{file.fileName}</Badge>
            </FileElement>
            <Gap bottom={5} />
          </div>
        ))}
    </FileItemsElement>
  );
}

export function ImportConfirmationModal() {
  const { t } = useTranslation('fileManagement');
  const {
    clientId,
    clientLevel,
    fileData,
    importFileModalVisibility,
    setImportFileModalVisibility,
  } = useImportConsentContext();
  const clientName = useConsentClientName();
  const [selectedTabId, setSelectedTabId] = useState(ConsentFileType.OPT_IN);

  const { push } = useHistory();

  const handleClose = () => {
    setImportFileModalVisibility(false);
  };

  const handleConfirm = () => {
    const url = generatePath(FileManagementRoute.CLIENT_IMPORT_UPLOAD, { clientLevel, clientId });
    push(url, { uploadFiles: fileData });
  };

  const handleSelectedTabChanged = (newSelectedTabId: ConsentFileType) => {
    setSelectedTabId(newSelectedTabId);
  };

  const totalNumOfEmails = (consentFileType: ConsentFileType): number => {
    return fileData
      .filter(file => file.status.consentType === consentFileType)
      .reduce((sum, f) => sum + (f.status as Green).emailCount, 0);
  };

  const numOfGreenFiles = (consentFileType: ConsentFileType): number => {
    return fileData.filter(file => {
      return file.status.type === RAGStatus.GREEN && file.status.consentType === consentFileType;
    }).length;
  };

  return (
    <Modal
      isOpen={importFileModalVisibility}
      onCancelButtonClicked={handleClose}
      onCloseButtonClicked={handleClose}
      onOkButtonClicked={handleConfirm}
      heading={t('analysis.bottomBar.import.modal.title')}
      okButtonLabel={t('analysis.bottomBar.import.modal.confirmButton')}
      variation="clean"
      size="medium"
      minWidth={600}
      minHeight={450}
    >
      <Tabs fillHeight={false} selectedTabId={selectedTabId} onSelectedTabChanged={handleSelectedTabChanged}>
        <TabBar topPadding={-2}>
          <Tab tabId={ConsentFileType.OPT_IN} badgeLabel={`${numOfGreenFiles(ConsentFileType.OPT_IN)}`}>
            {t(`analysis.bottomBar.import.modal.tab.${ConsentFileType.OPT_IN}`)}
          </Tab>
          <Tab tabId={ConsentFileType.OPT_OUT} badgeLabel={`${numOfGreenFiles(ConsentFileType.OPT_OUT)}`}>
            {t(`analysis.bottomBar.import.modal.tab.${ConsentFileType.OPT_OUT}`)}
          </Tab>
          <Tab
            tabId={ConsentFileType.CONFIRMED_OPT_IN}
            badgeLabel={`${numOfGreenFiles(ConsentFileType.CONFIRMED_OPT_IN)}`}
          >
            {t(`analysis.bottomBar.import.modal.tab.${ConsentFileType.CONFIRMED_OPT_IN}`)}
          </Tab>
        </TabBar>

        <TabView padding={20}>
          {
            <>
              <h4>{t(`analysis.bottomBar.import.modal.text.subHeading.${selectedTabId}`)}</h4>
              <Gap bottom={20} />
              <p>
                {t('analysis.bottomBar.import.modal.text.line1', {
                  totalNumOfEmails: totalNumOfEmails(selectedTabId).toLocaleString(),
                  clientName,
                  consentType: selectedTabId,
                })}
              </p>
              <Gap bottom={20} />
              <p>{t('analysis.bottomBar.import.modal.text.line2')}</p>
              <Gap bottom={20} />
              <ImportingFileList fileType={selectedTabId} />
            </>
          }
        </TabView>
      </Tabs>
    </Modal>
  );
}
